import { Eye } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { clearCmsPreviewToken } from "@/features/shop/cms/preview/cmsPreviewToken";

const formatDraftUpdatedAt = (value: string): string | null => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long", timeStyle: "short" }).format(date);
};

type CmsPreviewBannerProps = {
  pageCode: string
  draftUpdatedAt?: string | null
};

/**
 * Bandeau de l'aperçu d'un brouillon CMS : collé sous la barre du haut tant qu'on fait défiler la
 * boutique, pour qu'on ne prenne jamais le brouillon pour la page en ligne.
 */
export const CmsPreviewBanner = ({ pageCode, draftUpdatedAt }: CmsPreviewBannerProps) => {
  const queryClient = useQueryClient();
  const updatedAt = draftUpdatedAt ? formatDraftUpdatedAt(draftUpdatedAt) : null;

  const exitPreview = () => {
    clearCmsPreviewToken();
    queryClient.removeQueries({ queryKey: ["cmsPage", pageCode, "preview"] });
  };

  return (
    <div
      role="region"
      aria-label="Aperçu du brouillon"
      className="sticky top-14 z-30 flex flex-col gap-3 rounded-lg border-2 border-amber-500 bg-amber-100 px-4 py-3 text-amber-950 shadow-md sm:flex-row sm:items-center sm:justify-between dark:bg-amber-900 dark:text-amber-50"
    >
      <div className="flex items-start gap-3">
        <Eye className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div>
          <p className="font-semibold">
            Aperçu du brouillon — cette version n'est pas encore publiée
          </p>
          {updatedAt && (
            <p className="text-sm">
              Brouillon modifié le
              {" "}
              {updatedAt}
            </p>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={exitPreview}
        className="shrink-0 rounded-lg bg-amber-950 px-4 py-2 text-sm font-medium text-white hover:bg-amber-800 focus:outline-none focus:ring-4 focus:ring-amber-300 dark:bg-amber-50 dark:text-amber-950 dark:hover:bg-amber-200"
      >
        Quitter l'aperçu
      </button>
    </div>
  );
};
